/**
 * Minimale, dependency-vrije ZIP-schrijver.
 *
 * Gebruikt voor twee dingen: het .xlsx-bestand van de export (een xlsx is een
 * ZIP met XML erin) en het downloaden van foto's als één bestand.
 *
 * We schrijven met opslagmethode 0 (geen compressie), zodat er geen
 * deflate-implementatie nodig is. Voor JPEG's maakt dat toch niets uit: die
 * zijn al gecomprimeerd en worden door zippen niet kleiner.
 */

export interface ZipBestand {
  naam: string;
  inhoud: string | Uint8Array;
}

const CRC_TABEL = (() => {
  const tabel = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    tabel[i] = c >>> 0;
  }
  return tabel;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    c = CRC_TABEL[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

export function maakZip(bestanden: ZipBestand[], mimeType = 'application/zip'): Blob {
  const encoder = new TextEncoder();
  const nu = new Date();
  const dosTijd =
    (nu.getHours() << 11) | (nu.getMinutes() << 5) | Math.floor(nu.getSeconds() / 2);
  const dosDatum =
    ((nu.getFullYear() - 1980) << 9) | ((nu.getMonth() + 1) << 5) | nu.getDate();

  const lokaleDelen: Uint8Array[] = [];
  const centraleDelen: Uint8Array[] = [];
  let offset = 0;

  for (const bestand of bestanden) {
    const naamBytes = encoder.encode(bestand.naam);
    const dataBytes =
      typeof bestand.inhoud === 'string' ? encoder.encode(bestand.inhoud) : bestand.inhoud;
    const crc = crc32(dataBytes);

    const lokaleKop = new Uint8Array(30 + naamBytes.length);
    const lv = new DataView(lokaleKop.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true); // versie
    lv.setUint16(6, 0x0800, true); // UTF-8 bestandsnamen
    lv.setUint16(8, 0, true); // methode: stored
    lv.setUint16(10, dosTijd, true);
    lv.setUint16(12, dosDatum, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, dataBytes.length, true);
    lv.setUint32(22, dataBytes.length, true);
    lv.setUint16(26, naamBytes.length, true);
    lv.setUint16(28, 0, true);
    lokaleKop.set(naamBytes, 30);

    lokaleDelen.push(lokaleKop, dataBytes);

    const centraleKop = new Uint8Array(46 + naamBytes.length);
    const cv = new DataView(centraleKop.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, dosTijd, true);
    cv.setUint16(14, dosDatum, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, dataBytes.length, true);
    cv.setUint32(24, dataBytes.length, true);
    cv.setUint16(28, naamBytes.length, true);
    cv.setUint32(42, offset, true);
    centraleKop.set(naamBytes, 46);
    centraleDelen.push(centraleKop);

    offset += lokaleKop.length + dataBytes.length;
  }

  const centraleGrootte = centraleDelen.reduce((som, deel) => som + deel.length, 0);
  const eind = new Uint8Array(22);
  const ev = new DataView(eind.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, bestanden.length, true);
  ev.setUint16(10, bestanden.length, true);
  ev.setUint32(12, centraleGrootte, true);
  ev.setUint32(16, offset, true);

  // Alles in een aaneengesloten buffer: een Blob per fragment is trager
  // en levert typeproblemen op met de verschillende Uint8Array-varianten.
  const alleDelen = [...lokaleDelen, ...centraleDelen, eind];
  const totaleLengte = alleDelen.reduce((som, deel) => som + deel.length, 0);
  const archief = new Uint8Array(totaleLengte);
  let positie = 0;
  for (const deel of alleDelen) {
    archief.set(deel, positie);
    positie += deel.length;
  }

  return new Blob([archief], { type: mimeType });
}
